/**
 * Admin — CRUD danh mục "Loại báo cáo".
 *
 * Replaces the legacy Python enum; ops tự thêm/sửa/tạm ngưng loại báo
 * cáo. Mọi dropdown "Loại báo cáo" trong scheduled task form đọc từ
 * danh sách này (qua endpoint `/v2/admin/scheduled-tasks/output-types`
 * + `x-enum-labels`).
 */

import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Textarea from './shared/Textarea';
import Toggle from './shared/Toggle';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';
import Modal from './shared/Modal';
import Badge from './shared/Badge';

const EMPTY_FORM = {
    code: '',
    display_name: '',
    description: '',
    ttl_seconds: '',
    is_active: true,
    show_in_filter: true,
    sort_order: 0,
};

export default function AdminInsightCategories() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const data = await adminApi.listInsightCategories(true);
            setItems(Array.isArray(data) ? data : []);
            setError(null);
        } catch (e) {
            setError(e?.message || 'Không tải được danh sách');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (!success) return;
        const t = setTimeout(() => setSuccess(null), 4000);
        return () => clearTimeout(t);
    }, [success]);

    const handleCreate = () => setEditing({ ...EMPTY_FORM, _mode: 'create' });
    const handleEdit = (item) => setEditing({
        code: item.code,
        display_name: item.display_name,
        description: item.description || '',
        ttl_seconds: item.ttl_seconds ?? '',
        is_active: !!item.is_active,
        show_in_filter: item.show_in_filter !== false,
        sort_order: item.sort_order ?? 0,
        _mode: 'edit',
    });

    const handleDeactivate = async (code) => {
        if (!confirm(`Tạm ngưng loại "${code}"?\nRow lịch sử vẫn giữ nguyên, chỉ ẩn khỏi dropdown tạo mới.`)) return;
        try {
            await adminApi.deactivateInsightCategory(code);
            setSuccess(`Đã tạm ngưng "${code}"`);
            await load();
        } catch (e) {
            setError(e?.message || 'Không tạm ngưng được');
        }
    };

    const handleActivate = async (item) => {
        try {
            await adminApi.updateInsightCategory(item.code, { is_active: true });
            setSuccess(`Đã kích hoạt lại "${item.code}"`);
            await load();
        } catch (e) {
            setError(e?.message || 'Không kích hoạt được');
        }
    };

    const handleSave = async (form) => {
        const body = {
            display_name: form.display_name.trim(),
            description: form.description || null,
            ttl_seconds: form.ttl_seconds === '' || form.ttl_seconds === null ? null : Number(form.ttl_seconds),
            is_active: !!form.is_active,
            show_in_filter: !!form.show_in_filter,
            sort_order: Number(form.sort_order || 0),
        };
        try {
            if (form._mode === 'create') {
                await adminApi.createInsightCategory({ code: form.code.trim(), ...body });
                setSuccess(`Đã tạo "${form.code}"`);
            } else {
                await adminApi.updateInsightCategory(form.code, body);
                setSuccess(`Đã cập nhật "${form.code}"`);
            }
            setEditing(null);
            await load();
        } catch (e) {
            setError(e?.message || 'Lưu thất bại');
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h1 className="text-lg font-semibold text-gray-900">Loại báo cáo</h1>
                <Button onClick={handleCreate}>Tạo loại mới</Button>
            </div>

            {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" className="mb-4">{success}</Alert>}

            {loading ? (
                <Spinner size="lg" text="Đang tải..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    Chưa có loại báo cáo nào. Tạo loại mới để bắt đầu.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Mã</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Tên hiển thị</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Mô tả</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Hạn (giây)</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Thứ tự</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Hiện ở filter</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item) => (
                                <tr key={item.code} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.code}</td>
                                    <td className="px-4 py-3 font-medium text-gray-900">{item.display_name}</td>
                                    <td className="px-4 py-3 text-gray-600 text-xs max-w-md truncate" title={item.description || ''}>
                                        {item.description || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">
                                        {item.ttl_seconds == null ? 'không giới hạn' : item.ttl_seconds}
                                    </td>
                                    <td className="px-4 py-3 text-xs">{item.sort_order}</td>
                                    <td className="px-4 py-3">
                                        <Badge variant={item.is_active ? 'success' : 'secondary'}>
                                            {item.is_active ? 'Hoạt động' : 'Tạm ngưng'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge variant={item.show_in_filter !== false ? 'success' : 'secondary'}>
                                            {item.show_in_filter !== false ? 'Hiện' : 'Ẩn'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <Button variant="ghost" size="xs" onClick={() => handleEdit(item)}>Sửa</Button>
                                        {item.is_active ? (
                                            <Button variant="ghost" size="xs" className="text-amber-600"
                                                onClick={() => handleDeactivate(item.code)}>
                                                Tạm ngưng
                                            </Button>
                                        ) : (
                                            <Button variant="ghost" size="xs" className="text-green-600"
                                                onClick={() => handleActivate(item)}>
                                                Kích hoạt
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <CategoryEditorModal
                    initial={editing}
                    onSave={handleSave}
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
}


function CategoryEditorModal({ initial, onSave, onClose }) {
    const [form, setForm] = useState(initial);
    const isCreate = form._mode === 'create';

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const codeError = (() => {
        if (!isCreate) return null;
        if (!form.code) return 'Mã bắt buộc.';
        if (!/^[a-z][a-z0-9_]{2,31}$/.test(form.code)) {
            return 'Mã phải là chữ thường / số / dấu gạch dưới, bắt đầu bằng chữ, dài 3–32.';
        }
        return null;
    })();
    const nameError = form.display_name.trim() ? null : 'Tên hiển thị bắt buộc.';
    const hasError = !!(codeError || nameError);

    return (
        <Modal
            open
            title={isCreate ? 'Tạo loại báo cáo mới' : `Chỉnh sửa — ${form.code}`}
            onClose={onClose}
            size="lg"
        >
            <div className="p-5 space-y-4">
                <div>
                    <Input
                        label="Mã"
                        hint={isCreate ? 'Ví dụ: earnings_review. Không thể đổi sau khi tạo.' : 'Không thể chỉnh sửa.'}
                        value={form.code}
                        onChange={(e) => set('code', e.target.value)}
                        readOnly={!isCreate}
                        disabled={!isCreate}
                        className="font-mono"
                        placeholder="earnings_review"
                    />
                    {codeError && <p className="text-xs text-red-500 mt-1">{codeError}</p>}
                </div>
                <div>
                    <Input
                        label="Tên hiển thị"
                        value={form.display_name}
                        onChange={(e) => set('display_name', e.target.value)}
                        placeholder="Đánh giá KQKD"
                    />
                    {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
                </div>
                <Textarea
                    label="Mô tả"
                    rows={3}
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    placeholder="Phân tích báo cáo tài chính quý / năm"
                />
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Hạn dùng mặc định (giây)"
                        type="number"
                        min={0}
                        value={form.ttl_seconds}
                        onChange={(e) => set('ttl_seconds', e.target.value)}
                        hint="Bỏ trống = không giới hạn. Dùng khi output config không set ttl_seconds."
                        placeholder="86400"
                    />
                    <Input
                        label="Thứ tự hiển thị"
                        type="number"
                        value={form.sort_order}
                        onChange={(e) => set('sort_order', e.target.value)}
                        hint="Số càng nhỏ càng lên trên trong dropdown."
                    />
                </div>
                <div className="flex items-center gap-3">
                    <Toggle checked={!!form.is_active} onChange={(v) => set('is_active', v)} />
                    <span className="text-sm text-gray-700">Hoạt động</span>
                    <span className="text-xs text-gray-400 ml-2">
                        {form.is_active ? 'Hiển thị trong dropdown tạo task mới.' : 'Ẩn khỏi dropdown tạo mới; insight lịch sử giữ nguyên.'}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <Toggle checked={!!form.show_in_filter} onChange={(v) => set('show_in_filter', v)} />
                    <span className="text-sm text-gray-700">Hiện ở filter feed</span>
                    <span className="text-xs text-gray-400 ml-2">
                        {form.show_in_filter ? 'Xuất hiện trong dropdown "Loại báo cáo" trên trang Insights.' : 'Ẩn khỏi dropdown filter (insight lịch sử vẫn hiển thị).'}
                    </span>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                    <Button variant="secondary" onClick={onClose}>Huỷ</Button>
                    <Button disabled={hasError} onClick={() => onSave(form)}>
                        {isCreate ? 'Tạo' : 'Lưu'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
